import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import {
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  signInAnonymously,
  updateProfile,
  updateEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from 'firebase/auth';
import { auth } from '@/lib/firebase';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { migrateAnonymousUserData } from '@/services/dataMigrationService';
import { getErrorCode } from '@/utils/errors';

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, displayName?: string) => Promise<void>;
  logout: () => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  loginAnonymously: () => Promise<User>;
  setVotingInProgress: (inProgress: boolean) => void;
  updateUserProfile: (displayName: string) => Promise<void>;
  updateUserEmail: (email: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  createEmailAccountWithMigration: (email: string, password: string, displayName?: string) => Promise<void>;
  signInWithGoogleAndMigrate: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  // Track if we're in the middle of a voting process to prevent disruptive updates
  const isVotingRef = useRef(false);

  const login = async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      toast.success(t('auth.loginSuccess'));
    } catch (error) {
      console.error('Login error:', error);
      toast.error(t('auth.invalidCredentials'));
      throw error;
    }
  };

  const signup = async (email: string, password: string, displayName?: string) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);

      if (displayName && result.user) {
        await updateProfile(result.user, { displayName });
      }

      toast.success(t('auth.signupSuccess'));
    } catch (error) {
      console.error('Signup error:', error);
      let errorMessage = t('error.generic');
      const code = getErrorCode(error);

      if (code === 'auth/email-already-in-use') {
        errorMessage = t('auth.emailAlreadyInUse');
      } else if (code === 'auth/weak-password') {
        errorMessage = t('auth.passwordTooShort');
      } else if (code === 'auth/invalid-email') {
        errorMessage = t('auth.emailInvalid');
      }

      toast.error(errorMessage);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      toast.success(t('auth.logoutSuccess'));
    } catch (error) {
      console.error('Logout error:', error);
      toast.error(t('error.generic'));
      throw error;
    }
  };

  const loginWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      // Request profile scope to ensure we get the photoURL
      provider.addScope('profile');
      provider.addScope('email');

      await signInWithPopup(auth, provider);
      toast.success(t('auth.loginSuccess'));
    } catch (error) {
      console.error('Google login error:', error);
      if (getErrorCode(error) !== 'auth/popup-closed-by-user') {
        toast.error(t('error.generic'));
      }
      throw error;
    }
  };

  const loginAnonymously = async (): Promise<User> => {
    try {
      const result = await signInAnonymously(auth);
      // Don't show success toast for anonymous login as it's automatic
      return result.user;
    } catch (error) {
      console.error('Anonymous login error:', error);
      toast.error(t('error.generic'));
      throw error;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // Only update state if we're not in a critical voting process
      if (!isVotingRef.current) {
        setCurrentUser(user);
        setLoading(false);
      } else {
        // Still set loading to false, but don't update currentUser during voting
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const setVotingInProgress = (inProgress: boolean) => {
    isVotingRef.current = inProgress;
  };

  const updateUserProfile = async (displayName: string) => {
    if (!currentUser) {
      throw new Error('No user logged in');
    }

    try {
      await updateProfile(currentUser, { displayName });
      toast.success(t('profile.profileUpdated'));
    } catch (error) {
      console.error('Update profile error:', error);
      toast.error(t('error.generic'));
      throw error;
    }
  };

  const updateUserEmail = async (email: string) => {
    if (!currentUser) {
      throw new Error('No user logged in');
    }

    try {
      await updateEmail(currentUser, email);
      toast.success(t('profile.profileUpdated'));
    } catch (error) {
      console.error('Update email error:', error);
      toast.error(t('error.generic'));
      throw error;
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    if (!currentUser || !currentUser.email) {
      throw new Error('No user logged in or email not available');
    }

    try {
      // Re-authenticate user with current password
      const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
      await reauthenticateWithCredential(currentUser, credential);

      // Update password
      await updatePassword(currentUser, newPassword);
      toast.success(t('profile.passwordChanged'));
    } catch (error) {
      console.error('Change password error:', error);
      const code = getErrorCode(error);

      if (code === 'auth/wrong-password') {
        toast.error(t('profile.currentPasswordIncorrect'));
      } else if (code === 'auth/weak-password') {
        toast.error(t('auth.passwordTooShort'));
      } else {
        toast.error(t('error.generic'));
      }
      throw error;
    }
  };

  const createEmailAccountWithMigration = async (email: string, password: string, displayName?: string) => {
    if (!currentUser || !currentUser.isAnonymous) {
      throw new Error('This function is only for anonymous users');
    }

    try {
      const anonymousUserId = currentUser.uid;

      // Create new email account
      const result = await createUserWithEmailAndPassword(auth, email, password);

      if (displayName && result.user) {
        await updateProfile(result.user, { displayName });
      }

      // Migrate data from anonymous account
      const migrationResult = await migrateAnonymousUserData(anonymousUserId, result.user);

      if (migrationResult.success) {
        toast.success(t('auth.dataMigrationSuccess'));

        // Force a refresh of avatar and profile data after successful migration
        // Small delay to ensure Firestore data is available
        setTimeout(() => {
          // Trigger a re-render by updating the user state
          // This will cause useUserAvatar to re-fetch from Firestore
          setCurrentUser(result.user);
        }, 500);
      } else {
        console.error('Migration failed:', migrationResult.error);
        toast.error(t('auth.dataMigrationError'));
      }

      toast.success(t('auth.signupSuccess'));
    } catch (error) {
      console.error('Email account creation with migration error:', error);
      let errorMessage = t('error.generic');
      const code = getErrorCode(error);

      if (code === 'auth/email-already-in-use') {
        errorMessage = t('auth.emailAlreadyInUse');
      } else if (code === 'auth/weak-password') {
        errorMessage = t('auth.passwordTooShort');
      } else if (code === 'auth/invalid-email') {
        errorMessage = t('auth.emailInvalid');
      }

      toast.error(errorMessage);
      throw error;
    }
  };

  const signInWithGoogleAndMigrate = async () => {
    if (!currentUser || !currentUser.isAnonymous) {
      throw new Error('This function is only for anonymous users');
    }

    try {
      const anonymousUserId = currentUser.uid;

      const provider = new GoogleAuthProvider();
      provider.addScope('profile');
      provider.addScope('email');

      const result = await signInWithPopup(auth, provider);

      // Migrate data from anonymous account
      const migrationResult = await migrateAnonymousUserData(anonymousUserId, result.user);

      if (migrationResult.success) {
        toast.success(t('auth.dataMigrationSuccess'));

        // Force a refresh of avatar and profile data after successful migration
        // Small delay to ensure Firestore data is available
        setTimeout(() => {
          // Trigger a re-render by updating the user state
          // This will cause useUserAvatar to re-fetch from Firestore
          setCurrentUser(result.user);
        }, 500);
      } else {
        console.error('Migration failed:', migrationResult.error);
        toast.error(t('auth.dataMigrationError'));
      }

      toast.success(t('auth.loginSuccess'));
    } catch (error) {
      console.error('Google sign-in with migration error:', error);
      if (getErrorCode(error) !== 'auth/popup-closed-by-user') {
        toast.error(t('error.generic'));
      }
      throw error;
    }
  };



  const value = {
    currentUser,
    loading,
    login,
    signup,
    logout,
    loginWithGoogle,
    loginAnonymously,
    setVotingInProgress,
    updateUserProfile,
    updateUserEmail,
    changePassword,
    createEmailAccountWithMigration,
    signInWithGoogleAndMigrate,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
